/**
 * Product Tour — one-time spotlight walkthrough for invited users.
 *
 * Fires when the current user's `needs_onboarding_tour` flag is true (set
 * server-side by AdminInvitation.accept() — never for admin-created users).
 * Skippable at any step; finishing or skipping both PATCH the flag off via
 * /users/me/ so it never re-fires across devices/browsers.
 *
 * Targets sidebar rows via `[data-tour-id="sidebar-<routeId>"]` (see
 * packages/suite-chrome/src/SuiteSidebar.tsx) rather than hardcoded pixel
 * coordinates, so it tracks the sidebar's real layout (collapsed/expanded,
 * responsive) instead of drifting out of sync with it.
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchMe, patchMe } from "../api/preferences.api";

interface TourStep {
  /** Null for the final, untargeted "you're ready" screen — renders centered, no spotlight. */
  targetSelector: string | null;
  titleKey: string;
  bodyKey: string;
}

export const REPLAY_TOUR_EVENT = "weco:replay-tour";

const STEPS: TourStep[] = [
  {
    targetSelector: '[data-tour-id="sidebar-root"]',
    titleKey: "tour.welcomeTitle",
    bodyKey: "tour.welcomeBody",
  },
  {
    targetSelector: '[data-tour-id="sidebar-email"]',
    titleKey: "tour.emailTitle",
    bodyKey: "tour.emailBody",
  },
  {
    targetSelector: '[data-tour-id="sidebar-chat"]',
    titleKey: "tour.chatTitle",
    bodyKey: "tour.chatBody",
  },
  {
    targetSelector: '[data-tour-id="sidebar-planner"]',
    titleKey: "tour.plannerTitle",
    bodyKey: "tour.plannerBody",
  },
  {
    targetSelector: '[data-tour-id="sidebar-vault"]',
    titleKey: "tour.vaultTitle",
    bodyKey: "tour.vaultBody",
  },
  {
    targetSelector: null,
    titleKey: "tour.readyTitle",
    bodyKey: "tour.readyBody",
  },
];

function useElementRect(selector: string | null): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    let raf = 0;
    const measure = () => {
      const el = document.querySelector(selector);
      setRect(el ? el.getBoundingClientRect() : null);
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [selector]);

  return rect;
}

export const ProductTour: React.FC = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchMe();
        if (!cancelled && me.needs_onboarding_tour) {
          setActive(true);
        }
      } catch {
        // Not fatal — the tour just won't fire this load; it'll be offered
        // again on the next successful /users/me/ fetch since the flag
        // stays set server-side until explicitly cleared.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onReplay = () => {
      setStepIndex(0);
      setActive(true);
    };
    window.addEventListener(REPLAY_TOUR_EVENT, onReplay);
    return () => window.removeEventListener(REPLAY_TOUR_EVENT, onReplay);
  }, []);

  const step = active ? STEPS[stepIndex] : null;
  const rect = useElementRect(step?.targetSelector ?? null);
  const hasTarget = Boolean(step?.targetSelector) && Boolean(rect);

  const clearFlag = () => {
    patchMe({ needs_onboarding_tour: false }).catch(() => {
      // Best-effort — worst case the tour offers itself again next login.
    });
  };

  const handleNext = () => {
    if (stepIndex < STEPS.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      setActive(false);
      clearFlag();
    }
  };

  const handleSkip = () => {
    setActive(false);
    clearFlag();
  };

  if (!active || !step) return null;

  const spotlightPad = 8;
  const tooltipTop = hasTarget ? Math.min((rect as DOMRect).bottom + 16, window.innerHeight - 220) : window.innerHeight / 2 - 80;
  const tooltipLeft = hasTarget ? Math.min((rect as DOMRect).right + 16, window.innerWidth - 340) : window.innerWidth / 2 - 160;

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Product tour">
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ width: "100vw", height: "100vh" }}>
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hasTarget && (
              <rect
                x={(rect as DOMRect).x - spotlightPad}
                y={(rect as DOMRect).y - spotlightPad}
                width={(rect as DOMRect).width + spotlightPad * 2}
                height={(rect as DOMRect).height + spotlightPad * 2}
                rx={12}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.65)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      <div
        className="absolute w-80 p-4 rounded-xl shadow-2xl pointer-events-auto"
        style={{
          top: tooltipTop,
          left: tooltipLeft,
          // Not --bg-surface: the Liquid Glass theme points that at a
          // translucent surface, and this card sits on a 65%-black scrim,
          // so it showed the scrim through itself and read as grey.
          background: "var(--surface-overlay)",
          border: "1px solid var(--border)",
        }}
      >
        <p className="text-xs font-semibold mb-1" style={{ color: "var(--accent)" }}>
          {t("tour.stepCounter", { current: stepIndex + 1, total: STEPS.length })}
        </p>
        <h3 className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>
          {t(step.titleKey)}
        </h3>
        <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
          {t(step.bodyKey)}
        </p>
        <div className="flex justify-between items-center">
          <button
            onClick={handleSkip}
            className="text-xs font-medium hover:opacity-80"
            style={{ color: "var(--text-muted)" }}
          >
            {t("tour.skip")}
          </button>
          <button
            onClick={handleNext}
            className="px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ background: "var(--accent)", color: "var(--color-primary-foreground, #000)" }}
          >
            {stepIndex < STEPS.length - 1 ? t("tour.next") : t("tour.finish")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductTour;
